/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { MetaInfos } from 'components/metaInfos'
import { Slider } from '@material-ui/core'
import { browserCheck } from '@ta-interaktiv/browsercheck'
import { UpdateNote, Link } from '@ta-interaktiv/contentbricks'
import { scaleThreshold } from 'd3-scale'

const updateDate = '6. November 2020'

const f = new Intl.NumberFormat('de-CH')
const color = scaleThreshold<number, string>()
  .range(['#d3343f', '#e0694a', '#eb9552', '#f5bd5a', '#ffe462'].reverse())
  .domain([1, 25, 50, 75, 99])
const colorText = scaleThreshold<number, string>()
  .range(['#fff', '#fff', '#202346', '#202346', '#202346'].reverse())
  .domain([1, 25, 50, 75, 99])

// TODO Set the correct article ID here
const ARTICLE_ID = '314785781563'

interface TT {
  pos: [string, string]
  knt: string
  alt: string
  prop: number
  val: number
  valabs: number
  inh: number
}

export function AppComponent() {
  const [changeDate, setChangeDate] = useState<string>(updateDate)
  const [changeLastWeekDay, setChangeLastWeekDay] = useState<string>(updateDate)
  const [rawData, setRawData] = useState<dataElement[] | undefined>()
  const [drawData, setDrawData] = useState<dataElement[] | undefined>()
  const [bias, setBias] = useState(2)
  const [groupsize, setGroupsize] = useState(300)
  const [tt, settt] = useState<TT | undefined>()

  /*

  {pos:[0,0],knt:"ZH",}


  */

  useEffect(() => {
    if (!rawData) return
    const formated = rawData.map(d => {
      d.values.forEach(v => {
        v.prop = +(
          100 -
          (1 - 1 / (100000 / (v.perpop * bias))) ** groupsize * 100
        ).toFixed(3)
        v.propabs = +(
          100 -
          (1 - 1 / (100000 / (v.value * bias))) ** groupsize * 100
        ).toFixed(3)
      })
      return d
    })

    formated.forEach(f => {
      f.values
        .sort((a, b) => {
          const v1 = parseInt(a.key)
          const v2 = parseInt(b.key)
          return v1 > v2 ? 1 : v1 === v2 ? 0 : -1
        })
        .sort(a => {
          const ret = a.key === 'Alle' ? -1 : 0

          return ret
        })
    })
    formated.sort((a, b) => {
      if (a.key === 'CH') return -1
      if (b.key === 'CH') return 1
      return b.values[0].prop - a.values[0].prop
    })
    setDrawData(formated)
  }, [rawData, groupsize, bias])

  useEffect(() => {
    fetch(
      'https://storage.googleapis.com/ta-interaktiv/corona-dashboard/eventrisk.json'
    )
      .then(response => {
        return response.json()
      })
      .then(data => {
        const formatSwissDate = (datestring: Date) => {
          const options = { day: 'numeric', month: 'long', year: 'numeric' }
          return new Date(datestring)
            .toLocaleDateString('de-DE', options)
            .replace('/', '.')
        }

        const date = data.shift().date
        setChangeDate(formatSwissDate(new Date(date)))
        setRawData(data)

        const curr = new Date(date)
        const last = curr.getDate() - curr.getDay() // First day is the day of the month - the day of the week
        setChangeLastWeekDay(formatSwissDate(new Date(curr.setDate(last))))
      })
  }, [])
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>
              Berechnen Sie das Corona-Risiko für Veranstaltungen in Ihrem
              Kanton
            </h1>
            <h2>
              Wie wahrscheinlich ist es, dass sich mindestens eine mit
              Sars-CoV-2 infizierte Person an einem Anlass in Ihrer Region
              befindet? Finden Sie es mit unserer interaktiven Grafik heraus.
            </h2>
          </div>
        </div>
      </header>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <MetaInfos
            authors={['Marc Brupbacher', 'Sebastian Broschinski']}
            updated={changeDate}
          />
        </div>

        <div className='ui text container '>
          <p>
            Wie hoch ist das Risiko in einem Club mit 100 sehr jungen Personen
            im Kanton Aargau, dass sich ein Infizierter unter den Gästen
            befindet? Und wie hoch ist es bei einer Lesung mit 30 eher älteren
            Teilnehmenden im Kanton Schaffhausen? Diese Szenarien versuchen wir
            mit einer Wahrscheinlichkeitsberechnung abzuschätzen.
          </p>
          <p>
            Wir berücksichtigen dabei das kantonale Infektionsgeschehen in den
            verschiedenen Altersgruppen in den letzten sieben Tagen. Da der
            R-Wert in der Schweiz über 1 liegt, stecken im Schnitt alle
            Infizierten mindestens eine weitere Person an. Diese bemerken zuerst
            nicht, dass sie das Virus in sich tragen, und sind nach neusten
            wissenschaftlichen Erkenntnissen{' '}
            <Link href='784679826621'>bis zu fünf Tage</Link> vor Ausbruch der
            Symptome ansteckend. In dieser Zeit besuchen sie Veranstaltungen,
            das Büro und reisen mit den ÖV. Dazu kommen noch die gänzlich
            asymptomatischen Fälle, die wegen der fehlenden Krankheitszeichen
            nie in Isolation gehen, und auch symptomatische Fälle, die die
            Quarantäne ignorieren. Zudem bilden die bestätigten Fälle, auf die
            wir uns stützen, nur einen Teil der Infektionen ab; die Dunkelziffer
            dürfte um ein Vielfaches grösser sein.
          </p>
          <p>
            Unsere Ausgangslage in der Standardeinstellung ist eine extrem
            konservative Annahme der epidemiologischen Lage, da wir nur die
            letzten sieben Tage berücksichtigen und lediglich von zweimal so
            vielen Fällen wie gemeldet ausgehen. Aber selbst so sind die
            Risikoeinschätzungen erstaunlich hoch in vielen Kantonen – vor allem
            in den jüngeren Bevölkerungsgruppen. Sie können die Einstellungen
            der Gruppengrösse und der Dunkelziffer selber jederzeit ändern.
          </p>
        </div>
        {browserCheck.isMobile && (
          <div className='ui vertical relaxed segment mobSliders'>
            <div className='ui container '>
              <div className='sliderWrapper'>
                <div className='sliderGroup'>
                  <div className='label_'>
                    <div className='first'> Gruppengrösse</div>
                    <div className='second'>
                      Wie viele Personen nehmen teil?
                    </div>
                  </div>
                  <Slider
                    defaultValue={groupsize}
                    step={null}
                    marks={[
                      { value: 20, label: 20 },
                      { value: 50, label: '' },
                      { value: 100, label: '' },
                      { value: 200, label: '' },
                      { value: 300, label: '' },
                      { value: 500, label: '' },
                      { value: 1000, label: 1000 },
                      { value: 2000, label: 2000 },
                      { value: 3000, label: 3000 }
                    ]}
                    track={false}
                    valueLabelDisplay='on'
                    min={20}
                    max={3000}
                    onChange={(obj, val) => {
                      setGroupsize(+val)
                    }}
                  />
                </div>
                <div className='sliderGroup'>
                  <div className='label_'>
                    <div className='first'> Dunkelziffer</div>
                    <div className='second'>
                      Wie viele unentdeckte Fälle gibt es?
                    </div>
                  </div>
                  <Slider
                    defaultValue={bias}
                    step={1}
                    marks={[
                      { value: 1, label: 1 },
                      { value: 2, label: 2 },
                      { value: 3, label: 3 },
                      { value: 4, label: 4 },
                      { value: 5, label: 5 },
                      { value: 6, label: 6 },
                      { value: 7, label: 7 },
                      { value: 8, label: 8 },
                      { value: 9, label: 9 }
                    ]}
                    track={false}
                    min={1}
                    max={9}
                    valueLabelDisplay='on'
                    onChange={(obj, val) => {
                      setBias(+val)
                    }}
                    valueLabelFormat={x => x + 'x'}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        <div className='ui container tableHead'>
          <UpdateNote
            updating='Wird wöchentlich aktualisiert'
            current={'Zeitraum: Woche bis ' + changeLastWeekDay}
          />

          <div>
            Wie wahrscheinlich ist es, dass sich <b>mindestens 1 Infizierter</b>{' '}
            unter{' '}
            <b>{groupsize > 9999 ? f.format(groupsize) : groupsize} Personen</b>{' '}
            befindet?
          </div>
          <span className='second'>Wahrscheinlichkeit in %</span>
        </div>
        <div className='ui  container' style={{ position: 'relative' }}>
          {/* {drawData&& <AreaViz data={drawData}/>} */}
          <table className='onepergroup'>
            <thead>
              <tr>
                <td />
                <td colSpan={10} className='subline'>
                  Altersgruppen
                </td>
              </tr>
            </thead>
            <tbody>
              {drawData &&
                drawData.map((d, i) => {
                  return (
                    <React.Fragment key={i}>
                      {i === 0 && (
                        <tr className='key'>
                          <td className='key'>
                            <div />
                          </td>
                          {d.values.map((v, j) => {
                            return (
                              <td
                                key={j}
                                className='key'
                                style={{
                                  fontWeight: j === 0 ? 700 : 200
                                }}
                              >
                                <div>{v.key}</div>
                              </td>
                            )
                          })}
                        </tr>
                      )}
                      <tr>
                        <td className='rowLabel'>{d.key}</td>
                        {d.values.map((v, j) => {
                          return (
                            <td
                              key={j}
                              style={{
                                backgroundColor:
                                  v.prop === 0 ? '#F4F4F4' : color(v.prop),
                                color: colorText(v.prop)
                              }}
                              onMouseMove={(e: React.MouseEvent) => {
                                const tt_: TT = {
                                  pos: [
                                    e.nativeEvent.layerY + 8 + 'px',
                                    browserCheck.isMobile
                                      ? '20%'
                                      : e.nativeEvent.layerX + 5 + 'px'
                                  ],
                                  knt: d.key,
                                  alt: v.key,
                                  prop: v.prop,
                                  val: v.perpop,
                                  valabs: v.value,
                                  inh: +v.pop.toFixed(1)
                                }

                                settt(tt_)
                              }}
                              onMouseLeave={() => {
                                settt(undefined)
                              }}
                            >
                              {v.value > 0
                                ? v.prop > 1
                                  ? v.prop.toFixed(0)
                                  : '< 1'
                                : '-'}
                              {j === 0 && i === 0 && !browserCheck.isMobile && (
                                <i
                                  style={{
                                    color: colorText(v.prop)
                                  }}
                                >
                                  %
                                </i>
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    </React.Fragment>
                  )
                })}
              <tr className='source'>
                {!browserCheck.isMobile && <td />}
                <td
                  colSpan={10}
                  style={{ textAlign: 'left', lineHeight: '1.5em' }}
                >
                  – = Keine bestätigten Fälle in der jeweiligen
                  Bevölkerungsgruppe in der aktuellen Woche
                </td>
              </tr>

              <tr className='source'>
                {!browserCheck.isMobile && <td />}
                <td colSpan={10}>Quelle: BAG, eigene Berechnungen</td>
              </tr>
            </tbody>
          </table>
          {tt && (
            <div
              className='Tooltip'
              style={{ top: tt.pos[0], left: tt.pos[1] }}
            >
              <table>
                <tbody>
                  <tr>
                    <td>Kanton:</td> <td className='b'>{tt.knt}</td>
                  </tr>
                  <tr>
                    <td>Altersgruppe:</td> <td className='b'>{tt.alt}</td>
                  </tr>
                  <tr>
                    <td>Anzahl Personen:</td>{' '}
                    <td className='b'>
                      {tt.inh > 9999 ? f.format(tt.inh) : tt.inh}
                    </td>
                  </tr>
                  <tr>
                    <td>davon Fälle in 7 Tg.:</td>{' '}
                    <td className='b'>{tt.valabs}</td>
                  </tr>
                  <tr>
                    <td> pro 100'000:</td>{' '}
                    <td className='b'>{tt.val.toFixed(1)}</td>
                  </tr>
                  <tr className='prop'>
                    <td> Wahrscheinlichkeit:</td>{' '}
                    <td className='b'>{tt.prop.toFixed(2)}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <div className='sliderWrapper'>
            {!browserCheck.isMobile && (
              <>
                <div className='sliderGroup'>
                  <div className='label_'>
                    <div className='first'> Gruppengrösse</div>
                    <div className='second'>
                      Wie viele Personen nehmen teil?
                    </div>
                  </div>
                  <Slider
                    defaultValue={groupsize}
                    step={null}
                    marks={[
                      { value: 20, label: 20 },
                      { value: 50, label: '' },
                      { value: 100, label: '' },
                      { value: 200, label: '' },
                      { value: 300, label: '' },
                      { value: 500, label: '' },
                      { value: 1000, label: 1000 },
                      { value: 2000, label: 2000 },
                      { value: 3000, label: 3000 }
                    ]}
                    track={false}
                    valueLabelDisplay='on'
                    min={20}
                    max={3000}
                    onChange={(obj, val) => {
                      setGroupsize(+val)
                    }}
                  />
                </div>
                <div className='sliderGroup'>
                  <div className='label_'>
                    <div className='first'> Dunkelziffer</div>
                    <div className='second'>
                      Wie viele unentdeckte Fälle gibt es?
                    </div>
                  </div>
                  <Slider
                    defaultValue={bias}
                    step={1}
                    marks={[
                      { value: 1, label: 1 },
                      { value: 2, label: 2 },
                      { value: 3, label: 3 },
                      { value: 4, label: 4 },
                      { value: 5, label: 5 },
                      { value: 6, label: 6 },
                      { value: 7, label: 7 },
                      { value: 8, label: 8 },
                      { value: 9, label: 9 }
                    ]}
                    track={false}
                    min={1}
                    max={9}
                    valueLabelDisplay='on'
                    onChange={(obj, val) => {
                      setBias(+val)
                    }}
                    valueLabelFormat={x => x + 'x'}
                  />
                </div>
              </>
            )}
            <div className='sliderGroup example'>
              <div className='label_'>
                <div className='first'> Lesebeispiel</div>
                <div className='second'>
                  <p>
                    {' '}
                    Wären im Kanton Zürich an einer Veranstaltung mit{' '}
                    <b>
                      {groupsize > 9999 ? f.format(groupsize) : groupsize}{' '}
                      Personen
                    </b>{' '}
                    nur Teilnehmende in der Altersklasse 20 bis 29 Jahre
                    anwesend, würde die{' '}
                    <b>
                      Wahrscheinlichkeit{' '}
                      {drawData &&
                        drawData
                          .find(f => f.key === 'ZH')
                          ?.values.find(f => f.key === '20 - 29')
                          ?.prop.toFixed(0)}
                      %{' '}
                    </b>{' '}
                    betragen, dass mindestens <b>1 Infizierte/Infizierter</b>{' '}
                    darunter ist.
                  </p>
                  <p>
                    Oder anders gesagt: Von 100 Veranstaltungen in Zürich mit{' '}
                    {groupsize > 9999 ? f.format(groupsize) : groupsize}{' '}
                    Personen wären an{' '}
                    {drawData &&
                      drawData
                        .find(f => f.key === 'ZH')
                        ?.values.find(f => f.key === '20 - 29')
                        ?.prop.toFixed(0)}{' '}
                    Anlässen infektiöse Personen anwesend.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className='ui vertical segment'
        style={{ clear: 'both', padding: '0' }}
      />
      <Footer
        articleId={ARTICLE_ID}
        questions={[
          'Finden Sie die Berechnung hilfreich?',
          'Funktionierte die technische Umsetzung?'
        ]}
        credits={[
          ['Konzept und Umsetzung', 'Marc Brupbacher, Sebastian Broschinski'],
          ['Mitarbeit', 'Patrick Meier'],
          [
            'Inspiriert durch',
            '<a target="_blank" href="https://cos.gatech.edu/news/georgia-tech-researchers-release-county-level-calculator-estimate-risk-covid-19-exposure-us">Georgia Institute of Technology</a> und die Visualisierung von <a target="_blank" href="https://twitter.com/t_kurz">Thorsten Kurz</a>'
          ],
          [
            'Brechnung',
            ' Wir berechnen die Risiken mittels der Binomialverteilung. Die Daten der bestätigten Fälle in jeder Altersgruppe beziehen wir vom <a target="_blank" href="https://covid-19-schweiz.bagapps.ch/de-1.html">Bundesamt für Gesundheit</a>.'
          ]
        ]}
      />
    </div>
  )
}
